import type { Illustration, Skin } from '@/libs/campaigns/types';
import { framedImageIllustration, imageIllustration } from '@/libs/campaigns/illustrations';
import emptyFavorites from '@/public/assets/campaigns/halloween-2026/empty-favorites@1.5x.webp';
import infoBannerDesktop from '@/public/assets/campaigns/halloween-2026/info-banner-desktop@2x.webp';
import infoBannerMobile from '@/public/assets/campaigns/halloween-2026/info-banner-mobile@2x.webp';
import infoBannerTablet from '@/public/assets/campaigns/halloween-2026/info-banner-tablet@1.5x.webp';
import logoDesktop from '@/public/assets/campaigns/halloween-2026/logo-desktop@3x.webp';
import logoMobile from '@/public/assets/campaigns/halloween-2026/logo-mobile@3x.webp';
import popupPurchasedSc from '@/public/assets/campaigns/halloween-2026/popup-purchased-sc@2x.webp';

const ASSETS = '/assets/campaigns/halloween-2026';

// Animated art from Motion.Lab: a 540×600 frame (WebM; HEVC MOV for Safari, 540w; WebP poster at 1.5x),
// shown 360 design px wide, i.e. a 360×400 frame. Each frame is placed by measuring its art against
// the Figma art. The smoke fades out inside the frame, so it needs no bleed.
const motionLabIllustration = (name: string, boxHeight: number, frameTop: number): Illustration => ({
  type: 'layers',
  width: 360,
  height: boxHeight,
  layers: [{
    kind: 'video',
    video: { webm: `${ASSETS}/${name}.webm`, mov: `${ASSETS}/${name}-ios.mov`, poster: `${ASSETS}/${name}-poster@1.5x.webp` },
    x: 0,
    y: frameTop,
    width: 360,
    height: 400,
  }],
});

// "Existing account detected · Halloween": the sad pumpkin, its frame 44px above the Figma art
// box. The box keeps the 363px height of the previous still, so the duplicate account and the
// spending limit popups keep their text where it was.
const popupSadIllustration = motionLabIllustration('popup-sad', 363, -44);

// The redemption result popup puts its art 32px below its top edge and its title 26px below the
// art, while Figma starts the art box at the top edge and the title 336px below it (67745:464787,
// 67745:464776). A 278px box with the frame moved up by those 32px puts the art and the title
// where Figma has them, and the smoke reaches the popup's top edge.
const redemptionFailedIllustration = motionLabIllustration('popup-sad', 278, -44 - 32);
// "Sign in · Halloween": the happy pumpkin, its frame 57px above the Figma art box.
const redemptionSubmittedIllustration = motionLabIllustration('popup-happy', 278, -57 - 32);

// "Congratulations · Halloween": the triple-seven reel, its frame 44px above the Figma art box
// (which keeps the reel's bottom where Figma has it); the popup draws the "2 SC FREE" badge.
const registrationCongratulationsIllustration = motionLabIllustration('registration-congrats', 300, -44);

export const halloween2026Skin: Skin = {
  logo: {
    desktop: logoDesktop,
    mobile: logoMobile,
  },
  illustrations: {
    // Figma renders of the whole banner art frames (67395:501430 desktop, 68569:319084 tablet
    // at 767px wide, 67395:505711 mobile).
    lobbyInfoBannerDesktop: framedImageIllustration(infoBannerDesktop),
    lobbyInfoBannerTablet: framedImageIllustration(infoBannerTablet),
    lobbyInfoBannerMobile: framedImageIllustration(infoBannerMobile),
    // Figma "pop up img" (67740:344251): the art exported without its glow (Ellipse 15019,
    // a layer blur), which is drawn live under it. The 202×160 art plus the 24px gap to
    // the text take up layout space; the smoke and the glow overflow around it.
    emptyFavorites: {
      type: 'layers',
      width: 202,
      height: 184,
      layers: [
        {
          kind: 'glow',
          path: 'M421.81 244.345C421.81 274.92 387.637 299.707 345.483 299.707C303.329 299.707 182.5 230.035 182.5 199.46C182.5 168.885 303.329 188.984 345.483 188.984C387.637 188.984 421.81 213.77 421.81 244.345Z',
          transform: 'translate(124.75 90.2805) rotate(164.98) scale(1 -1) translate(-302.155 -241.1035)',
          gradient: { x1: 308.31, y1: 207.359, x2: 364.194, y2: 270.139, from: '#BD4DF9', to: '#FE5104' },
          // Figma's layer blur 182.5 exports as σ 91.25, but its renderer matches σ 79.
          blur: 79,
        },
        // Exported at 1.5x: the union of the art layers, from (-5, -29) of the art box.
        { kind: 'image', image: emptyFavorites, x: -5, y: -29, width: 325 / 1.5, height: 306 / 1.5 },
      ],
    },
    registrationCongratulations: registrationCongratulationsIllustration,
    duplicateAccount: popupSadIllustration,
    spendingLimitReached: popupSadIllustration,
    redemptionFailed: redemptionFailedIllustration,
    redemptionSubmitted: redemptionSubmittedIllustration,
    // Figma "pop up img/Gold" (67745:464709): one art for every screen size.
    gameNeedsPurchasedScDesktop: imageIllustration(popupPurchasedSc),
    gameNeedsPurchasedScMobile: imageIllustration(popupPurchasedSc),
  },
  // Figma "SideBar Row/Large" with "Game categories / halloween" and the lobby row "Halloween Specials" (67795:470222).
  category: {
    alias: 'halloween',
    icon: 'ghost',
    messages: {
      en: {
        sidebarLabel: 'Halloween',
        lobbyTitle: 'Halloween Specials',
      },
    },
  },
  // Figma: the Shop sidebar row with the "Promo" Beta Badge (67385:472591), and the
  // "Special Offers 🎃" / "Regular Shop 🎃" titles (67396:476123, 67609:120960).
  shop: {
    promoBadge: true,
    sectionTitleEmoji: '🎃',
    messages: {
      en: {
        promoBadge: 'Promo',
      },
    },
  },
  messages: {
    en: {
      logoAlt: 'SweepKing Halloween',
    },
  },
};
